import React, { useEffect, useState } from "react";
import { debounce } from "lodash";
import { useGetDriversMutation } from "../../../store/api/lumi/drivers";
import { getType } from "../../../utils/date";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { Empty, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useGetConceptsOptionsMutation } from "../../../store/api/lumi/concepts";
import {
    CartesianGrid,
    LabelList,
    ReferenceLine,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { useSelector } from "react-redux";

export function ScoreDriversWidget(props) {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [baseline, setBaseline] = useState(0);
    const [yDomain, setYDomain] = useState([0, 10]); // Default domain

    const [getConceptOptionMut] = useGetConceptsOptionsMutation();
    const [getDriver] = useGetDriversMutation();

    const labelFontSize = useSelector((state) => {
        const fontSizes = state.fontManager.fontSizes;
        const normalizedKey = Object.keys(fontSizes).find(
            (key) => key.trim() === "labelFontSize"
        );
        return fontSizes[normalizedKey] || 16;
    });

    async function getConceptsOptions() {
        let conceptFilter = { type: "top", limit: 100 };
        const { data: dataConcepts } = await getConceptOptionMut({
            id: props.id,
            concept_selector: conceptFilter,
            filter: props.filters,
        });
        props.setFilterDocCount(dataConcepts.filter_count);
    }

    async function getDriversData() {
        setIsLoading(true);
        const conceptFilter = getConceptFilter(props);

        const { data: dataConcepts } = await getDriver({
            id: props.id,
            score_field: props.selectedFieldDriverName,
            concept_selector: conceptFilter,
            filter: props.filters,
            match_type: getType(props.seletctedType),
        });

        const baseline =
            dataConcepts.length > 0 ? dataConcepts[0].baseline.toFixed(3) : 0;
        const processedData = dataConcepts.map(
            ({ name, match_count, average_score }) => ({
                name,
                match_count,
                average_score: average_score.toFixed(3),
            })
        );

        setBaseline(baseline);
        setData(processedData);

        const scores = processedData.map((d) => parseFloat(d.average_score));
        const minY = Math.min(...scores, baseline);
        const maxY = Math.max(...scores, baseline);
        const padding = (maxY - minY) * 0.1;
        setYDomain([minY - padding, maxY + padding]);

        setIsLoading(false);
    }

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const averageScore = payload[0].payload.average_score;
            const difference = (averageScore - baseline).toFixed(3);
            const formattedDifference =
                difference > 0 ? `+${difference}` : difference;

            return (
                <div
                    style={{
                        backgroundColor: "#ffffff",
                        padding: "5px 10px",
                        lineHeight: "10px",
                        border: "1px solid #cccccc",
                        borderRadius: "5px",
                        fontSize: "12px",
                    }}
                >
                    <p>
                        <strong>{payload[0].payload.name}</strong>
                    </p>
                    <p>Total Matches: {payload[0].payload.match_count}</p>
                    <p>Average Score: {averageScore}</p>
                    <p>Difference from overall score: {formattedDifference}</p>
                </div>
            );
        }
        return null;
    };

    const splitText = (text, maxLineLength) => {
        const words = text.split(" ");
        const lines = [];
        let currentLine = "";

        words.forEach((word) => {
            if ((currentLine + word).length > maxLineLength) {
                lines.push(currentLine);
                currentLine = word;
            } else {
                currentLine += currentLine ? ` ${word}` : word;
            }
        });

        if (currentLine) lines.push(currentLine);
        return lines.join("\n");
    };

    useEffect(() => {
        setIsLoading(true);
        if (props.selectedFieldDriverName) {
            const debouncedGetDrivers = debounce(getDriversData, 500);
            const debouncedGetConcepts = debounce(getConceptsOptions, 500);

            debouncedGetDrivers();
            debouncedGetConcepts();

            return () => {
                debouncedGetDrivers.cancel();
                debouncedGetConcepts.cancel();
            };
        }
    }, [
        props.seletctedType,
        props.conceptTypeDay,
        props.conceptList,
        props.selectedFieldDriverName,
        props.filters,
        props.seletctedType,
        props.selectedPageSize,
    ]);

    if (isLoading) {
        return (
            <Spin
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
        );
    }
    if (data.length === 0) {
        return (
            <Empty style={{ position: "absolute", top: "45%", right: "33%" }} />
        );
    }
    return (
        <div
            className="volume-compare"
            style={{ height: props.widget.layout.h * 39 - 150 }}
        >
            <ResponsiveContainer width="100%" height="100%">
                <ScatterChart
                    margin={{ top: 20, right: 20, left: 50, bottom: 50 }}
                >
                    <CartesianGrid stroke="#E3E9E2" />
                    <XAxis
                        type="number"
                        dataKey="match_count"
                        name="Total Matches"
                        tick={{
                            style: {
                                fontWeight: 300,
                                fontColor: "#090A09",
                                fontSize: 12,
                            },
                        }}
                        label={{
                            value: "Total Matches",
                            position: "insideBottom",
                            offset: -20,
                            fill: "#090A09",
                            fontSize: 12,
                            fontColor: "#090A09",
                            fontWeight: 500,
                        }}
                    />
                    <YAxis
                        type="number"
                        dataKey="average_score"
                        name="Average Score"
                        label={{
                            value: splitText(
                                `Average Score (${props.selectedFieldDriverName})`,
                                15
                            ),
                            angle: -90,
                            dx: -40,
                            position: "insideLeft",
                            fill: "#090A09",
                            style: {
                                textAnchor: "middle",
                                fontWeight: 500,
                                fontSize: 12,
                                fontColor: "#090A09",
                            },
                        }}
                        fill={"#090A09"}
                        domain={yDomain}
                        tick={{
                            style: {
                                fontWeight: 300,
                                fontColor: "#555B55",
                                fontSize: 12,
                            },
                        }}
                        tickFormatter={(tick) =>
                            (Math.round(tick * 200) / 200).toFixed(3)
                        }
                    />

                    <ReferenceLine
                        y={baseline}
                        stroke="#1D201E"
                        strokeWidth={1}
                        label={({ viewBox }) => {
                            const { x, y } = viewBox;
                            return (
                                <text
                                    x={x + 10} // Adjust positioning
                                    y={y - 5} // Adjust positioning
                                    fill="#1D201E"
                                    fontSize={12}
                                    fontWeight={400}
                                    textAnchor="start"
                                >
                                    Average: {baseline}
                                </text>
                            );
                        }}
                    />

                    <Tooltip
                        cursor={{ strokeDasharray: "3 3" }}
                        content={<CustomTooltip />}
                    />

                    <Scatter
                        name="Above Baseline"
                        data={data.filter((d) => d.average_score >= baseline)}
                        fill="#0F62FE"
                    >
                        <LabelList
                            dataKey="name"
                            position="top"
                            fill="#000000"
                            style={{
                                fontSize: 12,
                                fontWeight: 300,
                                color: "#000000",
                            }}
                        />
                    </Scatter>

                    <Scatter
                        name="Below Baseline"
                        data={data.filter((d) => d.average_score < baseline)}
                        fill="#F0552D"
                    >
                        <LabelList
                            dataKey="name"
                            position="top"
                            fill="#000000"
                            style={{
                                fontSize: 12,
                                fontWeight: 300,
                                color: "black",
                            }}
                        />
                    </Scatter>
                </ScatterChart>
            </ResponsiveContainer>
        </div>
    );
}
